import {
  NineActions,
  NineButton,
  NineHeading
} from "@9amhealth/wcl/generated/react";
import styled from "@emotion/styled";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React from "react";
import translate from "src/lib/translate";
import FileType from "src/constants/fileType";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import InDialog from "src/ui/components/InDialog/InDialog";
import Translate from "src/ui/components/Translate/Translate";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import FileUploadDocument from "../../FileUploadDocument/FileUploadDocument";
import WeightProofUploadDialogCubit from "./WeightProofUploadDialogCubit";

const Container = styled.div`
  max-width: 600px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: var(--space-md);
  text-align: center;
`;

const StepContent = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SmallText = styled.p`
  color: var(--color-c-80);
  margin: 0;
  margin-top: auto;
  text-align: center;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--Accessible-Blue, #3973da);
  cursor: pointer;
  font-weight: 500;
  padding: 0;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

const CenteredNineActions = styled(NineActions)`
  justify-content: center;
`;

const WeightProofUploadDialog: FC<{ returnUrl: string }> = ({ returnUrl }) => {
  const navigate = useGoToOrBack();
  const [
    { uploadedFileIds, uploadReady, showSkipConfirm, startDate, endDate },
    {
      setUploadedFileIds,
      setUploadReady,
      handleClose,
      handleSkip,
      handleContinue,
      handleRequestSkip,
      handleSkipConfirmClose
    }
  ] = useBlocNext(WeightProofUploadDialogCubit);

  return (
    <>
      {showSkipConfirm && (
        <AppDialog
          confirmText="weightProofUpload.skipConfirm.skip"
          cancelText="weightProofUpload.skipConfirm.backToUpload"
          confirmArrow
          onCancel={handleSkipConfirmClose}
          onClose={handleSkipConfirmClose}
          onConfirm={handleSkip}
        >
          <h5>
            <Translate msg="weightProofUpload.skipConfirm.title" />
          </h5>
          <p>
            <Translate msg="weightProofUpload.skipConfirm.description" />
          </p>
        </AppDialog>
      )}
      <InDialog
        popup
        title={translate("weightProofUpload.title")}
        returnUrl={returnUrl}
        onClose={() => handleClose(returnUrl, navigate)}
      >
        <Container>
          <StepContent>
            <NineHeading>
              <h4 className="as-h4-large">
                <Translate msg="weightProofUpload.step.title" />
              </h4>
            </NineHeading>
            <p>
              <Translate
                msg="weightProofUpload.step.description"
                variables={{ startDate, endDate }}
              />
            </p>
            <FileUploadDocument
              onFileIdsChange={setUploadedFileIds}
              onReadyStateChange={setUploadReady}
              fileType={FileType.PRESCRIPTION_PROOF_OF_WEIGHT}
              fileTags={["weight-proof"]}
              title={null}
              description={null}
              uploadLabel={
                <Translate msg="weightProofUpload.upload.uploadLabel" />
              }
              helperText={
                <Translate msg="weightProofUpload.upload.helperText" />
              }
            />
            <div>
              {uploadedFileIds.length > 0 && (
                <CenteredNineActions>
                  <NineButton
                    disabled={!uploadReady ? "true" : "false"}
                    onClick={() => handleContinue(returnUrl, navigate)}
                  >
                    <Translate msg="weightProofUpload.upload.sendAndComplete" />
                  </NineButton>
                </CenteredNineActions>
              )}
            </div>
            <SmallText>
              <Translate msg="weightProofUpload.missingDocumentation" />
              <br />
              <LinkButton onClick={handleRequestSkip}>
                <Translate msg="weightProofUpload.skipForNow" />
              </LinkButton>
            </SmallText>
          </StepContent>
        </Container>
      </InDialog>
    </>
  );
};

export default WeightProofUploadDialog;
